<template>
  <div :class="{ 'side-menu-novel-root': true, shrink: !expand }" @click="submit">
    <div v-if="novel">
      <div class="grid-container" :class="{ shrink: !expand }">
        <transition name="fade-fast">
          <image-view
            :key="coverImageVersion"
            :class="{ 'novel-image': true, shrink: !expand }"
            :src="imageKey"
            placeholder="/img/placeholders/novel.png"
          />
        </transition>
        <div class="novel-title" :class="{ shrink: !expand }" v-if="expand">
          {{ novel.title }}
        </div>
        <div
          class="work-status-button"
          :class="{ shrink: !expand }"
          :style="isHovered ? hoverStyle : workStatusButtonStyle"
          @mouseover="isHovered = true"
          @mouseleave="isHovered = false"
        >
          <span class="status-name">{{ translatedWorkStatusName }}</span>
          <span class="dropdown-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              stroke="currentColor"
              stroke-width="2"
              class="bi bi-chevron-down"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
              />
            </svg>
          </span>
        </div>
        <div v-if="showGenrePopup" class="genre-popup">
          <div class="genre-popup-arrow"></div>
          <div class="genre-popup-content">
            {{ $t("novelDetail.setGenreCategory") }}
            <button class="genre-popup-close-btn" @click.stop="hideGenrePopup">×</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import ImageView from "@/components/atoms/ImageView.vue";
import { Novel } from "@/lib/models";
import { workStatus } from "@/lib/novelSettings";
import { NovelSetting } from "@/lib/models/novel";
import {
  getTranslatedWorkStatusById,
  WORK_STATUS_COLORS,
  WORK_STATUS_HOVER_COLORS,
  WorkStatusId,
} from "@/lib/utils/novelSettingsHelper";

interface Data {
  isHovered: boolean;
  showGenrePopup: boolean;
}

interface Methods {
  submit: () => void;
  hideGenrePopup: () => void;
  isGenreOrCategoryUnset: () => boolean;
}

interface Computed {
  novel: Novel | null;
  imageKey?: string | null;
  coverImageVersion: number;
  currentWorkStatus: NovelSetting;
  translatedWorkStatusName: string;
  workStatusButtonStyle: { backgroundColor: string };
  hoverStyle: { backgroundColor: string };
  isGenreSettingPopupDisplayedForNovel: boolean;
}

interface Props {
  novelId: string;
  expand: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { ImageView },

  props: {
    novelId: String,
    expand: Boolean,
  },

  data() {
    return {
      isHovered: false,
      showGenrePopup: false,
    };
  },

  mounted() {
    // ジャンルまたはカテゴリが未設定かつ、ジャンル設定ポップアップが表示されていない場合
    if (this.isGenreOrCategoryUnset() && !this.isGenreSettingPopupDisplayedForNovel) {
      this.showGenrePopup = true;
    }
  },

  computed: {
    novel() {
      const { novelId } = this;
      const { getters } = this.$store;
      return getters["novelModule/novel"](novelId);
    },
    imageKey() {
      if (!this.novel) {
        return null;
      }

      if (this.novel && this.novel.image && this.novel.image.startsWith("file:")) {
        return `file:novels/${this.novelId}/cover.jpg`;
      }

      return this.novel.image;
    },
    coverImageVersion() {
      return this.$store.getters["novelModule/coverImageVersion"](this.novelId);
    },
    currentWorkStatus() {
      const { novel } = this;
      return workStatus.find((status) => status.id === novel.workStatus) || workStatus[0];
    },
    translatedWorkStatusName() {
      return getTranslatedWorkStatusById(this.$t, this.currentWorkStatus.id);
    },
    workStatusButtonStyle() {
      return {
        backgroundColor:
          WORK_STATUS_COLORS[this.currentWorkStatus.id as WorkStatusId] || WORK_STATUS_COLORS.inProgress,
      };
    },
    hoverStyle() {
      // ホバー時は少し暗くする（filter: brightness(0.85)相当の色）
      return {
        backgroundColor:
          WORK_STATUS_HOVER_COLORS[this.currentWorkStatus.id as WorkStatusId] ||
          WORK_STATUS_HOVER_COLORS.inProgress,
      };
    },
    isGenreSettingPopupDisplayedForNovel() {
      const { $store, novelId } = this;
      return $store.getters["generalModule/isGenreSettingPopupDisplayedForNovel"](novelId);
    },
  },

  methods: {
    submit() {
      // 表紙・work-status-button・タイトル等、side-menu-novel-root内のどこがクリックされても
      // 遷移先は詳細画面で共通のため、クリック位置による分岐は行わない
      const targetRoute = { name: "detail", params: { novelId: this.novelId } } as const;
      // 既に対象ルートの場合は何もしない
      if (this.$route.name === targetRoute.name && this.$route.params.novelId === String(targetRoute.params.novelId)) {
        return;
      }
      this.$router.push(targetRoute);
    },
    async hideGenrePopup() {
      this.showGenrePopup = false;
      // 表示中の作品のジャンル設定ポップアップが閉じられたことを記録
      await this.$store.dispatch("generalModule/addDisplayedGenreSettingPopupForNovel", this.novelId);
    },
    isGenreOrCategoryUnset() {
      return !this.novel.genre || !this.novel.category;
    },
  },

  watch: {
    novel: {
      handler(newVal) {
        if (newVal?.genre && newVal?.category) {
          this.showGenrePopup = false;
        }
      },
      deep: true,
    },
  },
});
</script>

<style lang="scss" scoped>
.side-menu-novel-root {
  position: relative;
  background-color: var(--color-background-normal);
  width: 100%;
  height: 110px;
  cursor: pointer;

  &.shrink {
    width: 60px;
    height: 85px;
  }
}

.grid-container {
  position: absolute;
  display: grid;
  grid-template-columns: 1fr 2fr;
  grid-template-rows: 1fr 30px;
  top: 10px;
  right: 0px;
  left: 16px;
  bottom: 10px;

  &.shrink {
    top: 0px;
    right: 0px;
    left: 0px;
    bottom: 0px;
  }
}

.novel-image {
  grid-row: 1/3;
  grid-column: 1/2;
  height: 100%;
  width: 100%;

  &.shrink {
    grid-row: 1/3;
    grid-column: 1/3;
  }
}

.novel-title {
  position: relative;
  height: 2rem;
  // 複数行でellipsisを使う`line-clamp`を利用
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  color: var(--color-text-normal);

  grid-row: 1/2;
  grid-column: 2/3;

  padding: 8px 8px 8px 8px;

  &.shrink {
    display: none;
  }
}

.work-status-button {
  display: inline-flex;
  align-items: center;
  position: absolute;
  bottom: 4px;
  left: 78px;
  background-color: var(--color-status-in-progress);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-xxs);
  padding: 4px 12px;
  border-radius: var(--radius-md);
  cursor: pointer;
  user-select: none;
  font-weight: var(--font-weight-bold);

  &.shrink {
    display: none;
  }
}

.dropdown-icon {
  margin-left: 4px;
  display: flex;
  align-items: center;
}

.bi {
  width: 10px;
  height: 10px;
  color: var(--color-text-on-fixed-bg);
}

.status-name {
  align-items: center;
  line-height: normal;
}

.genre-popup {
  position: absolute;
  background-color: var(--color-genre-popup);
  color: var(--color-text-on-fixed-bg);
  padding: 8px;
  border-radius: var(--radius-xs);
  cursor: default;
  z-index: 1;
  bottom: -40px;
  left: 80px;
  align-items: center;
}

.genre-popup-arrow {
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid var(--color-genre-popup);
  position: absolute;
  top: -7px;
  left: 25px;
}

.genre-popup-content {
  background-color: var(--color-genre-popup);
  font-size: var(--font-size-xxs);
  font-weight: var(--font-weight-bold);
}

.genre-popup-close-btn {
  background: none;
  border: none;
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-xs);
  cursor: pointer;
  font-weight: var(--font-weight-bold);
  padding: 0;
}
</style>
